import { createReducer } from "@reduxjs/toolkit";
import cloneDeep from "lodash/cloneDeep";
import { v4 as generateUuid } from "uuid";

import { P200, P500 } from "@atlaskit/theme/colors";

import { polyfillGetRandomValuesIfNecessary } from "../../../../utils/polyfills";
import { messages } from "../messages";

import {
    adjustProjectTimeline,
    addStory,
    setProjectTimeline,
    resetDemo,
    updateEpic,
    updateStory,
    Actions,
    completeDemo,
    mapProjectDependicies,
    AdjustProjectTimelineActionPayload,
    SetProjectTimelineActionPayload,
    setEditModeItem,
    unlinkDependency
} from "./actions";

export type InteractiveState = Actions;

/**
 * Represents a mapping between an interactive state and the current spotlight.
 */
export const actionSpotlightMap = new Map<
    InteractiveState,
    Spotlight | undefined
>([
    [
        Actions.UPDATE_EPIC,
        {
            header: messages.spotlightHeaderStage1,
            body: messages.spotlightBodyStage1,
            posX: 293,
            posY: 286
        }
    ],
    [
        Actions.ADD_STORY,
        {
            header: messages.spotlightHeaderStage2,
            body: messages.spotlightBodyStage2,
            posX: 270,
            posY: 286
        }
    ],
    [
        Actions.UPDATE_STORY,
        {
            header: messages.spotlightHeaderStage3,
            body: messages.spotlightBodyStage3,
            posX: 297,
            posY: 321
        }
    ],
    [
        Actions.SET_PROJECT_TIMELINE,
        {
            header: messages.spotlightHeaderStage4,
            body: messages.spotlightBodyStage4,
            posX: 495,
            posY: 321
        }
    ],
    [
        Actions.ADJUST_PROJECT_TIMELINE,
        {
            header: messages.spotlightHeaderStage5,
            body: messages.spotlightBodyStage5,
            posX: 505,
            posY: 321
        }
    ],
    [
        Actions.MAP_PROJECT_DEPENDENCIES,
        {
            header: messages.spotlightHeaderStage6,
            body: messages.spotlightBodyStage6,
            posX: 520,
            posY: 209
        }
    ],
    [
        Actions.RESET_DEMO,
        {
            header: messages.spotlightHeaderStage1,
            body: messages.spotlightBodyStage1,
            posX: 0,
            posY: 0
        }
    ],
    [Actions.COMPLETE_DEMO, undefined]
]);
polyfillGetRandomValuesIfNecessary();
/**
 * Represents the initial state of the interactive roadmap.  This is also used when the demo is reset.
 */
const initialEpicId = generateUuid();
export const initialState: RoadmapState = {
    editModeItemId: initialEpicId,
    isDemoComplete: false,
    interactive_state: Actions.UPDATE_EPIC,
    dependency_state: "UNLINKED",
    epics: [
        {
            id: generateUuid(),
            name: messages.epicName,
            mode: "VIEW",
            children: [
                {
                    id: generateUuid(),
                    name: messages.story1Name,
                    start: 2,
                    end: 3
                },
                {
                    id: generateUuid(),
                    name: messages.story2Name,
                    start: 2,
                    end: 3
                },
                {
                    id: generateUuid(),
                    name: messages.story3Name,
                    start: 3,
                    end: 4
                }
            ]
        },
        {
            id: initialEpicId,
            name: "",
            mode: "EDIT",
            children: []
        }
    ],
    spotlight: actionSpotlightMap.get(Actions.UPDATE_EPIC)
};

export const reducer = (state: RoadmapState) =>
    createReducer(state, (builder) =>
        builder
            .addCase(updateEpic, (state, { payload }) => {
                const interactive_state = state.isDemoComplete
                    ? Actions.COMPLETE_DEMO
                    : Actions.ADD_STORY;

                const newState = cloneDeep(state);
                const epic = newState.epics[payload.epicIndex] as Epic;
                epic.name = payload.epicName;
                epic.canClickAddButton =
                    epic.children.length < 5 && payload.epicIndex === 1;

                const spotlight = actionSpotlightMap.get(interactive_state);
                return {
                    ...newState,
                    interactive_state,
                    spotlight,
                    editModeItemId: undefined
                };
            })
            .addCase(addStory, (state) => {
                const editModeItemId = generateUuid();
                const interactive_state = Actions.UPDATE_STORY;
                // do not spotlight if demo is complete.
                const spotlight = state.isDemoComplete
                    ? undefined
                    : actionSpotlightMap.get(interactive_state);
                const newState = cloneDeep(state);
                const hideFromRoadmap =
                    (newState.epics[1] as Epic).children?.length > 0; // can only set project timeline for first story.

                newState.epics[1]?.children.push({
                    id: editModeItemId,
                    name: "",
                    start: 2,
                    end: 3,
                    color: P200,
                    isVisible: false,
                    hideFromRoadmap,
                    lineColor: P500
                });

                //  temporarily disable click add button if it has just been clicked.
                if (newState.epics[1]) {
                    newState.epics[1].canClickAddButton = false;
                }

                return {
                    ...newState,
                    interactive_state,
                    spotlight,
                    editModeItemId
                };
            })
            .addCase(updateStory, (state, { payload }) => {
                // go back to ADD_STORY if demo is complete
                const interactive_state = state.isDemoComplete
                    ? Actions.ADD_STORY
                    : Actions.SET_PROJECT_TIMELINE;

                // do not spotlight if demo is complete.
                const spotlight = state.isDemoComplete
                    ? undefined
                    : actionSpotlightMap.get(interactive_state);
                const newState = cloneDeep(state);

                // do not allow on click event if demo is complete.
                const onClickPayloadAction = state.isDemoComplete
                    ? undefined
                    : setProjectTimeline({
                          storyIndex: payload.storyIndex
                      });

                const name = payload.storyName;
                const canAdjust = true;

                const story = newState.epics[payload.epicIndex]?.children[
                    payload.storyIndex
                ] as Story;
                (newState.epics[payload.epicIndex] as Epic).children[
                    payload.storyIndex
                ] = {
                    ...story,
                    name,
                    canAdjust,
                    onClickPayloadAction
                };

                // only allow click add button if there are less than 5 stories and the demo is complete
                const epic = newState.epics[payload.epicIndex] as Epic;
                epic.canClickAddButton =
                    epic.children.length < 5 &&
                    payload.epicIndex === 1 &&
                    state.isDemoComplete;

                if (state.isDemoComplete) {
                    const story = epic?.children[payload.storyIndex] as Story;
                    story.canAdjust = true;
                    story.onClickPayloadAction = setProjectTimeline({
                        storyIndex: payload.storyIndex
                    });
                }

                return {
                    ...newState,
                    editModeItemId: undefined,
                    interactive_state,
                    spotlight
                };
            })
            .addCase(setProjectTimeline, (state, { payload }) => {
                const interactive_state = state.isDemoComplete
                    ? Actions.COMPLETE_DEMO
                    : Actions.ADJUST_PROJECT_TIMELINE;
                const spotlight = actionSpotlightMap.get(interactive_state);
                const newState = cloneDeep(state);
                const onMouseDownAndLeavePayloadAction = adjustProjectTimeline({
                    storyIndex: payload.storyIndex
                } as AdjustProjectTimelineActionPayload);

                const story = newState.epics[1]?.children[
                    payload.storyIndex
                ] as Story;
                const onClickPayloadAction = null;
                const isVisible = true;
                const canAdjust = state.isDemoComplete
                    ? state.dependency_state === "UNLINKED"
                    : true;

                if (newState.epics[1]) {
                    newState.epics[1].children[payload.storyIndex] = {
                        ...story,
                        onMouseDownAndLeavePayloadAction,
                        onClickPayloadAction,
                        isVisible,
                        canAdjust
                    };
                }

                return {
                    ...newState,
                    interactive_state,
                    spotlight
                };
            })
            .addCase(adjustProjectTimeline, (state, { payload }) => {
                const interactive_state = Actions.MAP_PROJECT_DEPENDENCIES;
                const spotlight = actionSpotlightMap.get(interactive_state);
                const newState = cloneDeep(state);
                if (newState.epics[1]) {
                    const story = newState.epics[1].children[
                        payload.storyIndex
                    ] as Story;
                    story.start = 2;
                    story.end = 4;
                    story.canAdjust = state.isDemoComplete ? true : false;
                    story.onMouseDownAndLeavePayloadAction = undefined;
                }

                return {
                    ...newState,
                    interactive_state,
                    spotlight
                };
            })
            .addCase(mapProjectDependicies, (state) => {
                const interactive_state = Actions.COMPLETE_DEMO;
                const spotlight = actionSpotlightMap.get(interactive_state);
                const newState = cloneDeep(state);
                return {
                    ...newState,
                    interactive_state,
                    spotlight,
                    dependency_state: "LINKED"
                };
            })
            .addCase(completeDemo, (state) => {
                const interactive_state = Actions.ADD_STORY;
                const newState = cloneDeep(state);
                if (newState.epics[1]) {
                    newState.epics[1].canClickAddButton = true;
                }
                return { ...newState, interactive_state, isDemoComplete: true };
            })
            .addCase(setEditModeItem, (state, { payload }) => {
                const newState = cloneDeep(state);
                return { ...newState, editModeItemId: payload.id };
            })
            .addCase(resetDemo, () => {
                const newState = cloneDeep(state);

                // generate new id to force re-render
                for (const epic of newState.epics) {
                    epic.id = generateUuid();
                    for (const story of epic.children) {
                        story.id = generateUuid();
                    }
                }

                return { ...newState, editModeItemId: newState.epics[1]?.id };
            })
            .addCase(unlinkDependency, (state) => {
                const newState = cloneDeep(state);

                // allow user to adjust the roadmap bars once stories are unlinked
                for (const epic of newState.epics) {
                    for (const story of epic.children) {
                        story.canAdjust = true;
                        story.onClickPayloadAction = setProjectTimeline({
                            storyIndex: Number(epic.children.indexOf(story))
                        } as SetProjectTimelineActionPayload);
                    }
                }

                return { ...newState, dependency_state: "UNLINKED" };
            })
    );

export type RoadmapState = {
    epics: Array<Epic>;
    interactive_state: InteractiveState;
    isDemoComplete: boolean;
    spotlight?: Spotlight;
    editModeItemId?: string;
    dependency_state: "UNLINKED" | "LINKED";
};

export type Spotlight = {
    header: { id: string; defaultMessage: string };
    body: { id: string; defaultMessage: string };
    posX: number;
    posY: number;
};

export type Epic = {
    id: string;
    name: string | { id: string; defaultMessage: string };
    mode: "EDIT" | "VIEW";
    children: Array<EpicChild>;
    canClickAddButton?: boolean;
};

export type EpicChild = Story;

export type Story = {
    id: string;
    name: string | { id: string; defaultMessage: string };
    start: number | null; // indicates the roadmap column this story begins.
    end: number | null; // indicates the roadmap column this story ends.
    color?: string;
    lineColor?: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onClickPayloadAction?: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onMouseDownAndLeavePayloadAction?: any;
    canAdjust?: boolean;
    isVisible?: boolean;
    hideFromRoadmap?: boolean;
};
